import { Link } from "react-router-dom";
import { Facebook, Youtube, Linkedin, Mail, Phone, MapPin, Camera } from "lucide-react";
import logo from "@/assets/lazona-logo.jpg";
import { Button } from "@/components/ui/button";
import { WARRANTY_TERMS } from "@/lib/businessSchema";
import { useLocation } from "react-router-dom";

// Custom Nextdoor icon component
const NextdoorIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
  </svg>
);

const residentialServices = [
  { name: "Travertine Restoration", href: "/travertine-restoration" },
  { name: "Marble Polishing", href: "/marble-polishing" },
  { name: "Tile & Grout Cleaning", href: "/tile-grout-cleaning" },
  { name: "Stone Sealing", href: "/stone-sealing" },
  { name: "Shower Restoration", href: "/services/shower-restoration" },
  { name: "Natural Stone Shower Restoration", href: "/natural-stone-shower-restoration" },
  { name: "Tile & Stone Repair", href: "/tile-stone-repair" },
  { name: "Countertop Restoration", href: "/countertop-restoration" },
  { name: "Grout Color Sealing", href: "/grout-color-sealing" },
  { name: "Grout Color Chart", href: "/grout-color-chart" },
  { name: "Headstone Restoration", href: "/headstone-restoration" },
];

const commercialServices = [
  { name: "Pool Deck Restoration", href: "/pool-deck-restoration" },
  { name: "Saltillo Tile Restoration", href: "/saltillo-tile-restoration" },
  { name: "Commercial Stone Floors", href: "/commercial/stone-floor" },
  { name: "Commercial Tile & Grout", href: "/commercial/tile-grout-restoration" },
  { name: "Cemetery & Mausoleum Care", href: "/commercial/cemetery-mausoleum-restoration" },
];

const quickLinks = [
  { name: "Home", href: "/" },
  { name: "Pricing", href: "/pricing" },
  { name: "Price Calculator", href: "/price-calculator" },
  { name: "Answers Hub", href: "/answers" },
  { name: "How to Clean Grout (Guide)", href: "/blog/how-to-clean-grout" },
  { name: "Grout Quiz", href: "/grout-quiz" },
  { name: "Photo Assessment", href: "/free-assessment" },
  { name: "Builders & Realtors", href: "/builder-realtor" },
  { name: "Travertine Gilbert", href: "/travertine-restoration-gilbert" },
  { name: "Service Areas", href: "/service-areas" },
  { name: "Blog", href: "/blog" },
  { name: "Testimonials", href: "/testimonials" },
  { name: "Project Case Studies", href: "/case-studies" },

  { name: "FAQ", href: "/faq" },
  { name: "Contact", href: "/contact" },
  { name: "Warranty", href: "/warranty" },
];

const serviceAreas = [
  { name: "Trilogy at Power Ranch, Gilbert", href: "/service-areas/trilogy-at-power-ranch-gilbert-az" },
  { name: "Las Sendas, Mesa", href: "/service-areas/las-sendas-mesa-az" },
  { name: "The Pecans, Queen Creek", href: "/service-areas/the-pecans-queen-creek-az" },
  { name: "Chandler (Home Base)", href: "/service-areas#chandler" },
  { name: "Gilbert", href: "/gilbert-tile-grout-restoration" },
  { name: "Mesa", href: "/service-areas#mesa" },
  { name: "Queen Creek", href: "/service-areas#queen-creek" },
  { name: "Scottsdale", href: "/service-areas#scottsdale" },
  { name: "Paradise Valley", href: "/service-areas#paradise-valley" },
  { name: "Ahwatukee", href: "/service-areas#ahwatukee" },
  { name: "Tempe", href: "/service-areas#tempe" },
  { name: "View All Areas →", href: "/service-areas" },
];

const popularArticles = [
  { name: "Porcelain Floors Impossible to Mop", href: "/blog/porcelain-floors-impossible-to-mop-arizona" },
  { name: "Grout Color Restoration Guide", href: "/blog/grout-color-restoration-arizona-guide" },
  { name: "Coatings for Man-Made Flooring", href: "/blog/coatings-for-manmade-flooring-arizona" },
  { name: "Hard Water Stains: Gilbert Showers", href: "/blog/hard-water-stains-gilbert-shower-tile" },
  { name: "Bleach Ruining Grout", href: "/blog/bleach-ruining-grout-arizona-bathrooms" },
  { name: "Vinegar Tile Cleaning Damage", href: "/blog/vinegar-tile-cleaning-damage" },
  { name: "Magic Eraser & Natural Stone", href: "/blog/magic-eraser-ruining-natural-stone-countertops" },
  { name: "Dish Soap Ruining Granite", href: "/blog/dish-soap-ruining-granite-countertops-arizona" },
  { name: "Shower Mold Dangers", href: "/blog/shower-mold-dangers" },
  { name: "Countertop Restoration vs Replacement", href: "/blog/countertop-restoration-vs-replacement-arizona" },
  { name: "Grout Repair vs Color Sealing Cost", href: "/blog/grout-repair-vs-color-sealing-cost-guide-arizona" },
  { name: "Color Sealing vs Retiling Cost", href: "/blog/grout-color-sealing-vs-retiling-arizona-bathroom-cost" },
  { name: "Tile & Grout Cleaning Gilbert AZ", href: "/tile-grout-cleaning-gilbert-az" },
  { name: "Remove Hard Water Stains: Shower", href: "/blog/remove-hard-water-stains-shower-arizona" },
  { name: "Maintain Modern Tile/Stone Shower", href: "/blog/how-to-maintain-modern-tile-stone-shower-arizona" },
  { name: "Modern Shower Restoration Case Study", href: "/blog/modern-tile-stone-shower-restoration-case-study-arizona" },
  { name: "Adora Falls Shower Restoration (Gilbert)", href: "/blog/adora-falls-tile-shower-restoration-gilbert-az" },
  { name: "Spot-Bonded Marmocer Marble (PV)", href: "/blog/spot-bonded-marmocer-marble-restoration-paradise-valley" },
  { name: "Arizona Travertine Needs Pro Sealer", href: "/blog/arizona-travertine-needs-professional-sealer" },
];

const socialLinks = [
  { name: "Facebook", href: "https://www.facebook.com/Lazonatilecare", icon: Facebook },
  { name: "YouTube", href: "https://www.youtube.com/@lazonatilecare", icon: Youtube },
  { name: "LinkedIn", href: "https://linkedin.com/company/lazona-stone-and-tile", icon: Linkedin },
  { name: "Nextdoor", href: "https://nextdoor.com/page/lazona-restorative-cleaners-chandler-az", icon: NextdoorIcon },
];

export function Footer() {
  const { pathname } = useLocation();
  const showInquiry = pathname !== "/contact" && !["/admin", "/oauth", "/privacy", "/terms", "/cookies", "/sms-terms", "/accessibility", "/login"].some(path => pathname.startsWith(path));
  const commercial = pathname.startsWith("/commercial");
  return (
    <footer className="bg-secondary text-secondary-foreground pb-20 md:pb-0">
      {showInquiry && <section aria-label="Arrange surface care" className="border-b border-border py-10">
        <div className="container-custom flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="max-w-xl">
            <h2 className="font-display text-2xl mb-3">{commercial ? "Preserve your property's first impression" : "Bring your tile and stone back to life"}</h2>
            <p className="text-secondary-foreground/80 text-sm">$150 consultation. Actual test spots are $250, credited toward your total if you book. Appointment requests are confirmed by our team.</p>
          </div>
          <div className="flex flex-wrap gap-3 lg:max-w-md">
            <Button asChild variant="gold"><a href="tel:4803520392"><Phone aria-hidden="true" />CALL NOW</a></Button>
            <Button asChild variant="goldOutline"><Link to="/contact#contact-form">Book A Consultation</Link></Button>
            {pathname !== "/free-assessment" && <Button asChild variant="goldOutline"><Link to="/free-assessment"><Camera aria-hidden="true" />Send Assessment Photos</Link></Button>}
          </div>
        </div>
      </section>}
      <div className="container-custom pt-8"><p className="text-sm text-secondary-foreground/80 leading-relaxed max-w-4xl">{WARRANTY_TERMS}</p></div>
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-8 lg:gap-6">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <img src={logo} alt="Lazona Tile Care logo, tile, grout and stone restoration in Chandler Arizona" className="h-28 md:h-32 w-auto bg-white rounded-xl p-3 shadow-lg" loading="lazy" width={200} height={128} />
            </Link>
            <p className="text-secondary-foreground/80 text-sm mb-4 leading-relaxed">
              Arizona's premier tile, grout, and natural stone specialists.
            </p>
            <div className="flex gap-2">
              {socialLinks.map((link) => (
                <a key={link.name} href={link.href} target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center text-secondary-foreground/80 hover:bg-accent hover:text-accent-foreground transition-all"
                  aria-label={`Visit us on ${link.name}`}>
                  <link.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Residential */}
          <div>
            <p className="font-display font-semibold text-base mb-4 tracking-wide">Residential</p>
            <ul className="space-y-2">
              {residentialServices.map((service) => (
                <li key={service.href}>
                  <Link to={service.href} className="text-sm text-secondary-foreground/70 hover:text-accent transition-colors">{service.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Commercial */}
          <div>
            <p className="font-display font-semibold text-base mb-4 tracking-wide">Commercial</p>
            <ul className="space-y-2">
              {commercialServices.map((service) => (
                <li key={service.href}>
                  <Link to={service.href} className="text-sm text-secondary-foreground/70 hover:text-accent transition-colors">{service.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <p className="font-display font-semibold text-base mb-4 tracking-wide">Service Areas</p>
            <ul className="space-y-2">
              {serviceAreas.map((area) => (
                <li key={area.href}>
                  <Link to={area.href} className="text-sm text-secondary-foreground/70 hover:text-accent transition-colors">{area.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <p className="font-display font-semibold text-base mb-4 tracking-wide">Quick Links</p>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-sm text-secondary-foreground/70 hover:text-accent transition-colors">{link.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Articles - reduces click depth to deep blog posts */}
          <div>
            <p className="font-display font-semibold text-base mb-4 tracking-wide">Popular Articles</p>
            <ul className="space-y-2">
              {popularArticles.map((article) => (
                <li key={article.href}>
                  <Link to={article.href} className="text-sm text-secondary-foreground/70 hover:text-accent transition-colors">{article.name}</Link>
                </li>
              ))}
              <li>
                <Link to="/blog/archive" className="text-sm font-medium text-accent hover:text-accent/80 transition-colors">Full Article Library</Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <p className="font-display font-semibold text-base mb-4 tracking-wide">Contact</p>
            <ul className="space-y-3">
              <li>
                <a href="tel:4803520392" className="flex items-center gap-2 text-sm text-secondary-foreground/70 hover:text-accent transition-colors">
                  <Phone className="w-4 h-4 text-accent" /> (480) 352-0392
                </a>
              </li>
              <li>
                <a href="mailto:lazonarestorative@gmail.com" className="flex items-center gap-2 text-sm text-secondary-foreground/70 hover:text-accent transition-colors">
                  <Mail className="w-4 h-4 text-accent" /> Email Us
                </a>
              </li>
              <li className="flex items-start gap-2 text-sm text-secondary-foreground/70">
                <MapPin className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                <address className="not-italic">
                  3100 W Ray Rd<br />
                  Chandler, AZ 85226
                </address>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10">
          <p className="text-center text-sm text-secondary-foreground/70 mb-4">
            Made for homeowners and facility managers based on real results from thousands of valued clients.
          </p>
          <p className="text-center text-xs text-secondary-foreground/60 mb-4 max-w-3xl mx-auto">
            By submitting any form on this site with the SMS opt-in box checked, you consent to receive text messages from Lazona Tile Care at the number provided. Message frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help. Mobile information is never shared with third parties for marketing.
          </p>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-secondary-foreground/60">© {new Date().getFullYear()} Lazona Tile Care. All rights reserved.</p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-secondary-foreground/60 justify-center">
              <Link to="/privacy" className="hover:text-accent transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-accent transition-colors">Terms of Service</Link>
              <Link to="/sms-terms" className="hover:text-accent transition-colors">SMS Terms</Link>
              <Link to="/cookies" className="hover:text-accent transition-colors">Cookie Policy</Link>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event('lazona:open-cookie-preferences'))}
                className="hover:text-accent transition-colors"
              >
                Cookie Preferences
              </button>
              <Link to="/accessibility" className="hover:text-accent transition-colors">Accessibility</Link>
            </div>
          </div>
          <p className="text-center text-[10px] text-secondary-foreground/30 mt-4" aria-hidden="true">
            citylocal101-verification: 86387gB6to4BpeM8IfdPy
          </p>


        </div>
      </div>
    </footer>
  );
}
